import { useState } from "react";
import { toast } from "sonner";

import { isAdoptableSuggestion } from "@/components/waste/transaction-card";
import type { RelabelSelection } from "@/components/waste/relabel-picker";
import type { WasteEvent } from "@/hooks/use-waste-events";
import {
  useAnalyseWasteEvent,
  useConfirmWasteEvent,
  useRelabelWasteEvent,
  type ConfirmedCost,
} from "@/hooks/use-waste-event-actions";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency } from "@/lib/format";

/**
 * Shared waste-event card actions (POS2-849). Owns the analyse/confirm/relabel
 * mutations, their toasts, the per-event pending flags, and the modal state
 * (lightbox, relabel picker, log/edit dialog, comments panel) so the
 * Transactions list and the single-event detail view drive identical behaviour
 * from one place instead of duplicating it. Render the modals with
 * `<WasteEventModals controller={...} />`.
 */
export type WasteEventActionsController = {
  onAnalyse: (eventId: string) => void;
  onConfirm: (event: WasteEvent) => void;
  isConfirming: (eventId: string) => boolean;
  isAnalysing: (eventId: string) => boolean;

  lightboxEvent: WasteEvent | null;
  setLightboxEvent: (event: WasteEvent | null) => void;
  relabelEvent: WasteEvent | null;
  setRelabelEvent: (event: WasteEvent | null) => void;
  editEvent: WasteEvent | null;
  setEditEvent: (event: WasteEvent | null) => void;
  commentsEvent: WasteEvent | null;
  setCommentsEvent: (event: WasteEvent | null) => void;
  logOpen: boolean;
  setLogOpen: (open: boolean) => void;

  handleRelabelSubmit: (selection: RelabelSelection) => void;
  relabelIsSubmitting: boolean;
};

// POS2-1134 CP1 localised the figure and left the sentence around it a literal, to land with the
// `waste` namespace later. POS2-1176 is later.
function costDescription(
  t: TFunction,
  locale: Locale,
  cost: ConfirmedCost | null,
): string | undefined {
  if (!cost) return undefined;
  return t("waste.costUpdated", { cost: formatCurrency(locale, cost.estimate, cost.currency) });
}

export function useWasteEventActionsController(): WasteEventActionsController {
  const { locale, t } = useTranslation();
  const confirmMutation = useConfirmWasteEvent();
  const relabelMutation = useRelabelWasteEvent();
  const analyseMutation = useAnalyseWasteEvent();

  const [lightboxEvent, setLightboxEvent] = useState<WasteEvent | null>(null);
  const [relabelEvent, setRelabelEvent] = useState<WasteEvent | null>(null);
  const [editEvent, setEditEvent] = useState<WasteEvent | null>(null);
  const [commentsEvent, setCommentsEvent] = useState<WasteEvent | null>(null);
  const [logOpen, setLogOpen] = useState(false);

  function onAnalyse(eventId: string) {
    analyseMutation.mutate(eventId, {
      onSuccess: (event: WasteEvent) => {
        const tier = event.classification_level;
        toast.success(t("waste.analysed"), {
          description: tier
            ? t("waste.classifiedAsTier", { tier })
            : t("waste.classificationComplete"),
        });
      },
      onError: () => toast.error(t("waste.analyseFailed")),
    });
  }

  function onConfirm(event: WasteEvent) {
    const onSuccess = (cost: ConfirmedCost | null) =>
      toast.success(t("waste.classificationConfirmed"), {
        description: costDescription(t, locale, cost),
      });
    const onError = () => toast.error(t("waste.confirmFailed"));

    // Confirming a boosted suggestion adopts it (relabel to the suggested FK):
    // spec §4 "always labelled" — a boost is a suggestion, so confirming it means
    // "yes, it was that item". Covers menu-matched / catalogue-grounded L3 and the
    // (rare) history-boosted L4 (POS2-962). Re-deriving on an unchanged id is
    // idempotent barring a menu edit between classify and confirm. A plain L4 or a
    // display-only AI suggestion falls through to a plain confirm of the label.
    if (isAdoptableSuggestion(event) && event.boosted_suggestion) {
      relabelMutation.mutate(
        { eventId: event.id, inventory_item_id: event.boosted_suggestion.id },
        { onSuccess, onError },
      );
    } else {
      confirmMutation.mutate(event.id, { onSuccess, onError });
    }
  }

  function handleRelabelSubmit(selection: RelabelSelection) {
    if (!relabelEvent) return;
    relabelMutation.mutate(
      { eventId: relabelEvent.id, ...selection },
      {
        onSuccess: (cost: ConfirmedCost | null) => {
          setRelabelEvent(null);
          toast.success(t("waste.reclassified"), { description: costDescription(t, locale, cost) });
        },
        onError: () => toast.error(t("waste.reclassifyFailed")),
      },
    );
  }

  function isConfirming(eventId: string): boolean {
    return (
      (confirmMutation.isPending && confirmMutation.variables === eventId) ||
      (relabelMutation.isPending && relabelMutation.variables?.eventId === eventId)
    );
  }

  function isAnalysing(eventId: string): boolean {
    return analyseMutation.isPending && analyseMutation.variables === eventId;
  }

  return {
    onAnalyse,
    onConfirm,
    isConfirming,
    isAnalysing,
    lightboxEvent,
    setLightboxEvent,
    relabelEvent,
    setRelabelEvent,
    editEvent,
    setEditEvent,
    commentsEvent,
    setCommentsEvent,
    logOpen,
    setLogOpen,
    handleRelabelSubmit,
    relabelIsSubmitting: relabelMutation.isPending,
  };
}
